/* @flow */

import React, {Component} from 'react';
import DownloadController from './components/Installation/DownloadController';
import HomeView from './components/HomeView/HomeView';
import IncompatibleOSView from './components/IncompatibleOSView/IncompatibleOSView';
import DialogManager from '@ccxi/ccxi-apps/src/stores/dialogManager';
import Config from '@ccxi/ccxi-apps/src/stores/config';
import {
	DEFAULT_TASK_ID,
	ALL_APPS_APP_PICKER_QUERY_PARAM,
	CCPP_APP_PICKER_QUERY_PARAM,
	CCPPN_APP_PICKER_QUERY_PARAM,
	LIST_IDS,
	GET_HELP_STRINGS,
	GET_HELP_OPEN_SOURCES,
	PAGE_TYPE,
	GET_HELP_CALLBACK,
	DOWNLOAD_CONTROLLER_USER_SOURCE,
	SUBSTANCE_TEXTURING_APP_PICKER_QUERY_PARAM,
	SUBSTANCE_COLLECTION_APP_PICKER_QUERY_PARAM
} from '../../constants/AppPicker/appPickerConstants';
import CSSUtil from '../../utils/CSSUtil';
import Url from '../../utils/Url';
import AppsUtil from '../../utils/AppsUtil';
import IncompatibleDialogUtil from '../../utils/IncompatibleDialogUtil';
import AppPickerUtil from '../../utils/AppPicker/AppPickerUtil';
import DeviceDetectionUtil from '../../utils/DeviceDetectionUtil';
import {error} from '../../components/ErrorToast/ErrorToast';
import AppPickerAnalytics from '../../analytics/AppPickerAnalytics';
import CompatibilityUtil from '../../utils/CompatibilityUtil';
import AppsAnalytics from '../../analytics/AppsAnalytics';
import HelpPopoverUtil from '../../utils/AppPicker/HelpPopoverUtil';

const ANALYTICS = {
	FFC_FALLBACK: 'ffc_fallback',
	CC_ICON: 'cc_icon',
	CHAT: 'chat',
	NO_APP_DATA_TOAST: 'no_app_data_toast'
};

type Props = {
	locale: string,
	environment: string,
	sapCodeDataFetchStatus: Array<Object>,
	getFFCDataForAppPicker: Function,
	preFetchedRestAPIResponses: Function,
	hasThor: boolean,
	location: Object,
	history: Object,
	imsData: Object, /* Overrides for analytics fields */
	intl: Object
};

type State = {
	selectedCategoryId: string,
	selectedTaskId: string,
	isIncompatibleOSScreenShown: boolean,
	showIncompatibleOSToast: boolean
};

export default class AppPickerController extends Component<Props, State> {
	enableHelpTimeouts: boolean;

	constructor(props: Props) {
		super(props);
		this.state = {
			selectedCategoryId: AppPickerUtil.getDefaultCategoryId(),
			selectedTaskId: DEFAULT_TASK_ID,
			isIncompatibleOSScreenShown: false, // Used to ensure the Incompatible OS screen is shown only once
			showIncompatibleOSToast: false // Used to control that the Incompatible OS toast on the App Picker screen is shown only once
		};
		this.enableHelpTimeouts = !DeviceDetectionUtil.isNotMacOrWin();
	}

	componentDidMount() {
		/*  Init the ccxi-apps dialog manager,
			since we are not using <CCXUI > where it was originally initialized.
		*/
		DialogManager.init(Config.dialogManager, this);
		AppPickerUtil.addAppPickerClassesAndEvents(this.ccIconEvent, this.chatEvent, this.handleCCEnter);
		// Fetch FFC data
		const allSapCodesToFetch = AppPickerUtil.getAllCategoriesSapCodes();
		const sapCodesToFetchFirst = AppPickerUtil.getDefaultCategorySapCodes();
		sapCodesToFetchFirst.push('KCCC');
		const sapCodesToFetchNext = allSapCodesToFetch.filter(sapCode => !sapCodesToFetchFirst.includes(sapCode));
		this.props.getFFCDataForAppPicker(sapCodesToFetchFirst, sapCodesToFetchNext, this.props.preFetchedRestAPIResponses);
		const appsAnalytics = new AppsAnalytics(this.props.environment);
		AppsUtil.setVisitorGuid(appsAnalytics.getVisitorGuid());
		HelpPopoverUtil.setCallback(GET_HELP_CALLBACK.VIEW_DESKTOP_APPS, this.viewYourDesktopAppsLinkClicked);
	}

	componentDidUpdate(prevProps) {
		CSSUtil.addAppPickerClassToAppContainer();
		if (prevProps.location.pathname !== this.props.location.pathname) {
			/* Close Get Help popup if it is already openend */
			HelpPopoverUtil.closeGetHelpPopover();
		}
	}

	componentWillUnmount() {
		AppPickerUtil.removeAppPickerClassesAndEvents(this.ccIconEvent, this.chatEvent, this.handleCCEnter);
	}

	ccIconEvent = (autoFocusListItem?: boolean) => {
		const heading = this.props.intl.formatMessage({id: GET_HELP_STRINGS.CC_CLICK_FAQ_HEADING});
		const updatingList = {
			listId: LIST_IDS.FAQ_AND_HELP,
			heading
		};
		HelpPopoverUtil.updateHelpPopoverConfig(undefined, [updatingList]);
		HelpPopoverUtil.openGetHelpPopover(GET_HELP_OPEN_SOURCES.CC_LOGO_CLICK, autoFocusListItem);
		this.handleAnalyticsEvent(ANALYTICS.CC_ICON);
	}

	handleCCEnter = (event: Object) => {
		if (event.key === 'Enter') {
			this.ccIconEvent(true);
		}
	}

	chatEvent = () => {
		this.handleAnalyticsEvent(ANALYTICS.CHAT);
	}

	onCategorySelect = (category: string) => {
		if (this.state.selectedCategoryId !== category) {
			this.onTaskSelect(DEFAULT_TASK_ID);
		}
		this.setState({selectedCategoryId: category});
	}

	onTaskSelect = (task: string) => {
		if (this.state.selectedTaskId !== task) {
			this.setState({selectedTaskId : task});
		}
	};

	viewYourDesktopAppsLinkClicked = () => {
		// This is called on the click of a link inside the Incompatible OS Screen
		this.setState({
			isIncompatibleOSScreenShown : true,
			showIncompatibleOSToast : !DeviceDetectionUtil.isNotMacOrWin()
		});
		HelpPopoverUtil.closeGetHelpPopover();
	};

	clearHelpTimeouts = () => {
		this.enableHelpTimeouts = false;
	};

	handleAnalyticsEvent(eventType: string) {
		const analytics = new AppPickerAnalytics(this.props.environment);
		analytics.initAnalytics();
		const event = {};
		const ui = {
			view_type : analytics.getPageTypeString() // eslint-disable-line camelcase
		};
		const content = {};
		const subcategory = this.isDownloadWorkflow() ? 'Download' : 'Welcome';
		switch (eventType) {
			case ANALYTICS.FFC_FALLBACK:
				event.type = 'api-error';
				event.subtype = 'ffc';
				break;
			case ANALYTICS.CC_ICON:
				event.subcategory = subcategory;
				event.type = 'click';
				event.subtype = 'cclogo';
				break;
			case ANALYTICS.CHAT:
				event.subcategory = subcategory;
				event.type = 'click';
				event.subtype = 'chat';
				break;
			case ANALYTICS.NO_APP_DATA_TOAST:
				event.subcategory = subcategory;
				event.type = 'render';
				event.subtype = 'app-toast';
				event.value = 'No 32 bit App version downloaded';
				content.type = analytics.getCompatibilityString(false, false);
				break;
		}
		analytics.genericAnalyticEvent({event, ui, content});
	}

	handleIncompatibileAnalytics = (sapCode: string, appFamilyName: string, eventObj?: Object = {}, contentObj?: Object = {}) => {
		const analytics = new AppPickerAnalytics(this.props.environment);
		analytics.initAnalytics();
		const event = {
			type: 'click',
			subcategory: 'Welcome:Learn More',
			value: CompatibilityUtil.isAnyOlderVersionSupported(sapCode) ? null : 'No OS version supported',
			...eventObj
		};
		const content = {
			id: sapCode,
			name: appFamilyName,
			type: analytics.getCompatibilityString(
				CompatibilityUtil.isAppSupported(sapCode),
				CompatibilityUtil.isAnyOlderVersionSupported(sapCode)
			),
			version: CompatibilityUtil.getSupportedAppVersion(sapCode),
			category: 'App Card',
			...contentObj
		};
		const ui = {
			view_type : analytics.getPageTypeString() // eslint-disable-line camelcase
		};
		analytics.genericAnalyticEvent({event, content, ui});
	}

	onIncompatibleOSToastDismiss = () => {
		this.setState({
			showIncompatibleOSToast : false
		});
	}

	showIncompatibilityDialog = (sapCode: string) => {
		if (!IncompatibleDialogUtil.appPickerIncompatibleDialog) {
			const ffcProductData = AppsUtil.getFFCProductConfigBySapCode(sapCode);
			if (ffcProductData || AppPickerUtil.isNujM3PhotoPlanTestExperience()) {
				const platform = (ffcProductData && AppsUtil.getProductPlatform(ffcProductData)) || 'desktop';
				const appModel = {
					sapCode,
					platform,
					appFamilyName: (ffcProductData && AppsUtil.getFFCProductConfigBySapCode(sapCode).displayName) || AppPickerUtil.getDisplayName(sapCode),
					systemRequirementLink: AppPickerUtil.getSystemRequirementLink(sapCode),
					handleIncompatibileAnalytics: this.handleIncompatibileAnalytics
				};
				IncompatibleDialogUtil.renderAppPickerIncompatibleDialog(appModel);
			}
			else {
				this.handleAnalyticsEvent(ANALYTICS.NO_APP_DATA_TOAST);
				error(this.props.intl.formatMessage({id: 'apps.pdp.noFFCDataError.CCXUI'}), {msgId: 'notEligible', variant: 'default', timeout:7000});
			}
		}
	}

	isDownloadWorkflow() {
		const pathDataCCWeb = AppsUtil.getCCWebPathData();
		return ((pathDataCCWeb.isPDP
			&& pathDataCCWeb.product)
			|| Url.getParam('productId'))
			&& Url.getParam('action') === 'install'
			&& (Url.getParam('source') === ALL_APPS_APP_PICKER_QUERY_PARAM
				|| Url.getParam('source') === CCPPN_APP_PICKER_QUERY_PARAM
				|| Url.getParam('source') === CCPP_APP_PICKER_QUERY_PARAM
				|| Url.getParam('source') === SUBSTANCE_TEXTURING_APP_PICKER_QUERY_PARAM
				|| Url.getParam('source') === SUBSTANCE_COLLECTION_APP_PICKER_QUERY_PARAM
			);
	}

	render() {
		const {selectedCategoryId, selectedTaskId, isIncompatibleOSScreenShown, showIncompatibleOSToast} = this.state;
		const {history, intl, imsData, hasThor, locale, environment, sapCodeDataFetchStatus} = this.props;
		if (this.isDownloadWorkflow()) {
			const productId = AppsUtil.getCCWebPathData().product || Url.getParam('productId');
			AppPickerUtil.setPageType(PAGE_TYPE.DOWNLOAD);
			return (
				<DownloadController
					key='download-view'
					source={DOWNLOAD_CONTROLLER_USER_SOURCE.APP_PICKER_DOWNLOAD_PAGE}
					productId={productId}
					history={history}
					locale={locale}
					environment={environment}
					intl={intl}
					showIncompatibilityDialog={this.showIncompatibilityDialog}
					hasThor={hasThor}
					clearHelpTimeouts={this.clearHelpTimeouts}
					openGetHelpWidget={true}
				/>
			);
		}
		else if (AppPickerUtil.isNUJDeprecatedOS() && !isIncompatibleOSScreenShown) {
			AppPickerUtil.setPageType(PAGE_TYPE.INCOMPATIBLE_OS_SCREEN);
			return <IncompatibleOSView
				key='incompatible-os-view'
				environment={environment}
				locale={locale}
				history={history}
				intl={intl}
				hasThor={hasThor}
				imsData={imsData}
				viewYourDesktopAppsLinkClicked={this.viewYourDesktopAppsLinkClicked}
				clearHelpTimeouts = {this.clearHelpTimeouts}
				enableHelpTimeouts = {this.enableHelpTimeouts}
			/>;
		}
		AppPickerUtil.setPageType(PAGE_TYPE.APP_PICKER);
		return <HomeView
			key='home-view'
			environment={environment}
			locale={locale}
			history={history}
			intl={intl}
			hasThor={hasThor}
			imsData={imsData}
			onTaskSelect={this.onTaskSelect}
			onCategorySelect={this.onCategorySelect}
			selectedCategoryId={selectedCategoryId}
			selectedTaskId={selectedTaskId}
			showIncompatibleOSToast={showIncompatibleOSToast}
			onIncompatibleOSToastDismiss={this.onIncompatibleOSToastDismiss}
			renderOnlyCategories={AppPickerUtil.shouldRenderOnlyCategories()}
			showIncompatibilityDialog = {this.showIncompatibilityDialog}
			sapCodeDataFetchStatus = {sapCodeDataFetchStatus}
			enableHelpTimeouts = {this.enableHelpTimeouts}
			clearHelpTimeouts={this.clearHelpTimeouts}
		/>;
	}
}
